import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
} from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { AuthService } from '@core/services/auth.service';
import { BaseDataService } from '@core/services/base-data.service';
import { DestroyBase } from '@capturum/shared';
import { SettingIndexedDbModel } from '@capturum/complete';
import { tap } from 'rxjs/operators';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { BaseDataKey } from '@core/enums/base-data-key.enum';
import { Country } from '@features/leads/enums/country.emum';
import { Locale } from '../../../../../public/questionnaire/services/public-locale.service';
import { environment } from '@environments/environment';
import { HostnameService } from '@core/services/hostname.service';

@Component({
    selector: 'app-footer',
    templateUrl: './footer.component.html',
    styleUrls: ['./footer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FooterComponent extends DestroyBase implements OnInit {
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly authService = inject(AuthService);
  protected readonly baseDataService = inject(BaseDataService);
  protected readonly cdr = inject(ChangeDetectorRef);
  private readonly hostnameService = inject(HostnameService);

  public isVisible = input<boolean>(false);

  public currentLang: string = Locale.NL;
  public currentEnv: string | boolean;

  public countries = toSignal(this.baseDataService.getBaseDataValues(BaseDataKey.COUNTRY));

  public selectedLanguageForFilesDownloading = computed(() => {
    const countries = this.countries();

    if (countries) {
      const currentUserCountry = countries.find((item) => {
        return item.id === this.authService.getUser()?.country_base_data_value_id;
      })?.value;

      if (currentUserCountry === Country.BENELUX) {
        return this.currentLang;
      }

      if (currentUserCountry?.toLowerCase() === 'gr') {
        return Locale.EL;
      }

      if (currentUserCountry?.toLowerCase() === 'uk') {
        return Locale.EN;
      }

      if (!currentUserCountry) {
        return environment.domainLanguages[this.hostnameService.hostname] ?? Locale.EN;
      }

      return currentUserCountry?.toLowerCase();
    } else {
      return environment.domainLanguages[this.hostnameService.hostname] ?? Locale.EN;
    }
  });

  constructor() {
    super();
    this.currentLang = this.translateService.currentLang;
  }

  public ngOnInit(): void {
    this.checkForLanguageChange();
    this.currentEnv = SettingIndexedDbModel.getValue('general.tenant_name');
  }

  private checkForLanguageChange(): void {
    this.translateService.onLangChange
      .pipe(
        tap((langEvent: LangChangeEvent) => {
          this.currentLang = langEvent.lang;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }
}
